import React from 'react'
// libs
import { useDispatch, useSelector } from 'react-redux'
import { change, formValueSelector } from 'redux-form'
// components
import RecordPicker from './RecordPicker'
import { useInjuryMasterRecordNames } from './useInjuryMasterRecordNames'
// constants
import { Forms } from 'constants/forms'
// types
import { IField, IInjuryMasterIncidentProperty } from 'types/graphql'
// views
import { ValueMapTitle, ValueMapWrapper } from './views'

interface Props {
  name: string
  property: IInjuryMasterIncidentProperty
  sourceField: IField
}

const selector = formValueSelector(Forms.SYSTEM_PROCESS_CREATE_FORM)

// Which Injury Master record each option of the Skefto pick list stands for. Unlike a choice, Injury
// Master's own records have no static list to match by name, so every option needs picking by hand.
const RecordMap: React.FC<Props> = ({ name, property, sourceField }) => {
  const dispatch = useDispatch()

  const recordMap: Record<string, { value: string; label?: string } | string> | undefined = useSelector((state) =>
    selector(state, `${name}.recordMap`)
  )

  // Captured once — a saved mapping's entries start as raw URLs; the effect below turns them into
  // options, so re-reading the live value on every render would lose the URLs to resolve.
  const initialEntriesRef = React.useRef(
    Object.entries(recordMap ?? {}).flatMap(([from, entry]) =>
      typeof entry === 'string' ? [{ from, url: entry }] : []
    )
  )

  const recordNames = useInjuryMasterRecordNames(initialEntriesRef.current.map(({ url }) => url))

  // Saved mappings only hold the URL — show the record's real name once `recordNames` has it. Only
  // dispatches when the field doesn't already hold that exact value — see FixedValue.tsx for why
  // that check matters (an unconditional dispatch here can loop forever).
  React.useEffect(() => {
    initialEntriesRef.current.forEach(({ from, url }) => {
      const label = recordNames[url] ?? url
      const current = recordMap?.[from]
      const currentOption = typeof current === 'string' ? undefined : current

      if (currentOption?.value !== url || currentOption?.label !== label) {
        dispatch(change(Forms.SYSTEM_PROCESS_CREATE_FORM, `${name}.recordMap.${from}`, { value: url, label }))
      }
    })
  }, [dispatch, name, recordMap, recordNames])

  const options = sourceField.options ?? []

  if (!options.length || !property.listType) {
    return null
  }

  return (
    <ValueMapWrapper>
      <ValueMapTitle>Match each option to an Injury Master record</ValueMapTitle>
      {options.map((option) => (
        <RecordPicker
          isRequired={false}
          key={option.id}
          label={option.label}
          listType={property.listType}
          name={`${name}.recordMap.${option.id}`}
        />
      ))}
    </ValueMapWrapper>
  )
}

export default RecordMap
